import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { TutorBillingRow } from './interfaces'

import { useAdminRoutes } from '@shared/lib/routes'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingQuery } from './tutor-billing-query.hook'
import { getTutorBillingTutorName } from '../lib/tutor-billing-fields'

interface TableTutorBillingReturn {
  rows: ComputedRef<TutorBillingRow[]>
  isLoading: ComputedRef<boolean>
  hasRows: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
  getTutorName: (row: TutorBillingRow) => string
  getTutorUrl: (row: TutorBillingRow) => string
  getAccountHolder: (row: TutorBillingRow) => string
  getAccount: (row: TutorBillingRow) => string
  getCountry: (row: TutorBillingRow) => string
  setOpenView: (row: TutorBillingRow) => void
}

export const useTableTutorBilling = (): TableTutorBillingReturn => {
  const route = useRoute()
  const store = useTutorBillingStore()
  const { setQuery } = useTutorBillingQuery()

  const rows = computed<TutorBillingRow[]>(() => store.getRows)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasRows = computed<boolean>(() => store.hasRows)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const getTutorUrl = (row: TutorBillingRow): string => useAdminRoutes('admin.tutor.show', { tutor_id: row.tutor.id }) ?? ''

  const getAccountHolder = (row: TutorBillingRow): string => row.payment.account_holder_name || '—'

  const getAccount = (row: TutorBillingRow): string => row.payment.account || '—'

  const getCountry = (row: TutorBillingRow): string => row.personal.nationality || '—'

  const setOpenView = (row: TutorBillingRow): void => {
    store.openModal(TutorBillingModal.View, row)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await setQuery({
      ...route.query,
      page: String(value),
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await setQuery({
      ...route.query,
      per_page: String(value),
      page: undefined,
    })
  }

  return {
    rows,
    isLoading,
    hasRows,
    meta,
    perPageOptions,
    setChangePage,
    setChangePerPage,
    getTutorName: getTutorBillingTutorName,
    getTutorUrl,
    getAccountHolder,
    getAccount,
    getCountry,
    setOpenView,
  }
}
